import { type TransitionProps } from "notistack";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

/**
 * iOS-style easing curves matching the app's Drawer/SidePanel transitions.
 * See src/appV2/redesign/components/Drawer.tsx
 */
const EASING_ENTER = "cubic-bezier(0.2, 0.9, 0.3, 1)";
const EASING_EXIT = "cubic-bezier(0.3, 0, 0.5, 1)";
const DURATION_ENTER_MS = 380;
const DURATION_EXIT_MS = 300;

/** Matches the Berlin md breakpoint used in Snackbar.module.scss */
const MOBILE_MAX_WIDTH = 639;

interface SlideTransitionProps extends TransitionProps {
  children: ReactElement;
}

/**
 * Returns the translate offset based on screen width.
 * On mobile (top-center), slides down from above (-16px).
 * On desktop (bottom-right), slides up from below (+16px).
 */
function getSlideOffset(): string {
  const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;
  return isMobile ? "translateY(-16px)" : "translateY(16px)";
}

/**
 * Custom slide + fade transition for notistack snackbars.
 * Direction adapts to screen size: slides down on mobile (top), slides up on desktop (bottom-right).
 */
export function SlideDownTransition(props: SlideTransitionProps) {
  const { in: isOpen, children, onEnter, onEntered, onExit, onExited, style } = props;
  const nodeRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(isOpen);

  const applyEnterStyles = useCallback(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    const offset = getSlideOffset();

    node.style.transition = "none";
    node.style.transform = offset;
    node.style.opacity = "0";

    // Force reflow to ensure the initial styles are applied before transitioning
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    node.scrollHeight;

    node.style.transition = [
      `transform ${DURATION_ENTER_MS}ms ${EASING_ENTER}`,
      `opacity ${DURATION_ENTER_MS}ms ${EASING_ENTER}`,
    ].join(", ");
    node.style.transform = "translateY(0)";
    node.style.opacity = "1";
  }, []);

  const applyExitStyles = useCallback(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    const offset = getSlideOffset();

    node.style.transition = [
      `transform ${DURATION_EXIT_MS}ms ${EASING_EXIT}`,
      `opacity ${DURATION_EXIT_MS}ms ${EASING_EXIT}`,
    ].join(", ");
    node.style.transform = offset;
    node.style.opacity = "0";
  }, []);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    if (isOpen) {
      onEnter?.(node, true);
      applyEnterStyles();
      onEntered?.(node, true);
      return;
    }

    onExit?.(node);
    applyExitStyles();

    const timer = setTimeout(() => {
      onExited?.(node);
      setMounted(false);
    }, DURATION_EXIT_MS);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!mounted) {
    return null;
  }

  return (
    <div
      ref={nodeRef}
      style={{
        ...style,
        willChange: "transform, opacity",
      }}
    >
      {children}
    </div>
  );
}
